import React, { useEffect, useRef, useState } from 'react';
import {
  deleteSelfAssessmentAttachmentForStandard,
  getSelfAssessmentAttachmentForStandard,
  uploadSelfAssessmentAttachmentForStandard,
} from '../apiCaller';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../helpers/showMessage';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import { ButtonWithIcon } from '../../../style/styled.selfAssessment';
import ImageOverlay from 'lqa-ui/lib/Components/ImageOverlay';
import { Image } from '../../../app/common/icons/Image';
import ImageResize from '../../../helpers/ImageResize';
import { AddPhoto } from '../../../app/common/icons/AddPhoto';

export interface IAttachment {
  name: string;
  type: string;
  id: number;
  url?: string;
  notes?: string;
}
export interface IStandardAttachment {
  duplicateCategoryId: number;
  templateStandardId: number;
  attachments: IAttachment[];
}

export interface IStandardAttachmentProps {
  assessmentId: number;
  duplicateCategoryId: number;
  parentId: number;
  templateStandardId: number;
  standardsAttachment: IStandardAttachment[];
  standardsAttachmentsIds: number[];
  refreshSelfAssessmentAttachmentsListOfStandards: () => void;
}

export function StandardAttachment ({
  assessmentId,
  duplicateCategoryId,
  parentId,
  templateStandardId,
  standardsAttachment,
  standardsAttachmentsIds,
  refreshSelfAssessmentAttachmentsListOfStandards,
}: IStandardAttachmentProps) {
  const uploadRef = useRef<any>('');
  const patternImage = /image-*/;

  const [uploadedImage, setUploadedImage] = useState<IAttachment | undefined>(
    undefined
  );
  const [isAPICallCompleted, setIsAPICallCompleted] = useState<boolean>(false);
  const [isShowLoader, setIsShowLoader] = useState<boolean>(false);

  const getStandardAttachment = () => {
    setIsAPICallCompleted(false);
    getSelfAssessmentAttachmentForStandard(
      assessmentId,
      duplicateCategoryId,
      templateStandardId
    )
      .then((response) => {
        if (response && response.length > 0) {
          setUploadedImage(response[0]);
        }
        setIsAPICallCompleted(true);
        refreshSelfAssessmentAttachmentsListOfStandards();
      })
      .catch((e) => {
        setIsAPICallCompleted(true);
      });
  };

  useEffect(() => {
    if (standardsAttachment && standardsAttachment.length > 0) {
      const findStandardAttachment = standardsAttachment.find((attachment) => {
        return (
          Number(attachment.templateStandardId) === templateStandardId &&
          Number(attachment.duplicateCategoryId) === duplicateCategoryId
        );
      });
      if (findStandardAttachment) {
        setIsAPICallCompleted(true);
        setUploadedImage(findStandardAttachment.attachments[0]);
      } else {
        setIsAPICallCompleted(true);
      }
    } else {
      if (!standardsAttachmentsIds.length) {
        setIsAPICallCompleted(true);
      }
    }
  }, [standardsAttachment]);

  const handleStandardUpload = (templateStandardId, file) => {
    const isImage = file.type.match(patternImage);
    if (!file || file.size > 209715200 || !isImage) {
      showErrorMessage(' Invalid file. Please check file size or file type.');
      return;
    }
    setIsShowLoader(true);
    const formData = new FormData();
    formData.append('file', file);
    uploadSelfAssessmentAttachmentForStandard(
      assessmentId,
      duplicateCategoryId,
      templateStandardId,
      formData
    )
      .then((response) => {
        showSuccessMessage('Attachment uploaded successfully.');
        getStandardAttachment();
        if (parentId) {
          uploadSelfAssessmentAttachmentForStandard(
            parentId,
            duplicateCategoryId,
            templateStandardId,
            formData
          );
        }
        setIsShowLoader(false);
      })
      .catch((error) => {
        showErrorMessage(error.response.data.message);
        setIsShowLoader(false);
      });
  };

  const deleteStandardAttachment = (fileData) => {
    setIsShowLoader(true);
    deleteSelfAssessmentAttachmentForStandard(
      assessmentId,
      duplicateCategoryId,
      templateStandardId,
      fileData.id,
      fileData.name
    )
      .then(() => {
        showSuccessMessage('Attachment deleted successfully.');
        getStandardAttachment();
        if (parentId) {
          deleteSelfAssessmentAttachmentForStandard(
            parentId,
            duplicateCategoryId,
            templateStandardId,
            fileData.id,
            fileData.name
          );
        }
        setIsShowLoader(false);
      })
      .catch((e) => {
        showErrorMessage('Something went wrong.');
        setIsShowLoader(false);
      });
  };

  if (!isAPICallCompleted) {
    return null;
  }

  if (isShowLoader) {
    return <LoadingContent layout="flex" />;
  }

  if (uploadedImage) {
    const image = [{ source: uploadedImage.url as string }];
    return (
      <ButtonWithIcon backgroundColor="verdigris" borderColor="verdigris">
        <ImageOverlay
          images={image}
          showRemoveIcon={true}
          onRemove={(_, imageIndex) => {
            if (uploadedImage[imageIndex]) {
              deleteStandardAttachment(uploadedImage[imageIndex]);
            }
          }}
          target={
            <span>
              <Image strokeColor="platinum" />
            </span>
          }
        />
      </ButtonWithIcon>
    );
  }
  return (
    <ImageResize
      onImageCompletion={(file) =>
        handleStandardUpload(templateStandardId, file)
      }
      uploadImageComponent={
        <ButtonWithIcon>
          <AddPhoto />
          <input
            ref={uploadRef}
            type="file"
            id="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (
                e.target &&
                e.target.files &&
                e.target.files[0] &&
                e.target.files[0]
              ) {
                handleStandardUpload(templateStandardId, e.target.files[0]);
              }
              uploadRef.current.value = null;
            }}
          />
        </ButtonWithIcon>
      }
    />
  );
}
